import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Typography,
  CircularProgress,
  Autocomplete,
  Chip,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { habitService } from '../services/api';
import { LogHabitRequest, HabitDefinition } from '../types/api';
import { getCategoryColor } from '../utils/habitColors';
import LoadingSpinner from './LoadingSpinner';
import { MarkdownRenderer } from './MarkdownText';

interface HabitLogFormProps {
  onSuccess?: () => void;
}

const HabitLogForm: React.FC<HabitLogFormProps> = ({ onSuccess }) => {
  const [habits, setHabits] = useState<Record<string, HabitDefinition>>({});
  const [selectedHabit, setSelectedHabit] = useState('');
  const [selectedChildHabit, setSelectedChildHabit] = useState('');
  const [count, setCount] = useState('');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingHabits, setLoadingHabits] = useState(true);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Load habit definitions on component mount
  useEffect(() => {
    loadHabits();
  }, []);

  const loadHabits = async () => {
    try {
      const response = await habitService.getDefinitions();
      if (response.success && response.data) {
        setHabits(response.data);
      } else {
        setAlert({ type: 'error', message: response.error || 'Failed to load habits' });
      }
    } catch (error) {
      setAlert({ type: 'error', message: 'Failed to connect to server' });
    } finally {
      setLoadingHabits(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const allowsNegativeCount = effectiveHabitDef?.goal.type === 'AVERAGE';
    
    if (!selectedHabit || !count) {
      setAlert({ type: 'error', message: 'Please select a habit and enter a count' });
      return;
    }

    const countValue = parseFloat(count);
    if (isNaN(countValue) || (!allowsNegativeCount && countValue < 0)) {
      setAlert({
        type: 'error',
        message: allowsNegativeCount ? 'Please enter a valid number' : 'Please enter a valid number (0 or greater)'
      });
      return;
    }

    setLoading(true);
    setAlert(null);

    try {
      const habitIdToLog = selectedChildHabit || selectedHabit;
      const request: LogHabitRequest = {
        habitId: habitIdToLog,
        count: countValue,
        comment: comment.trim() || undefined,
      };

      const response = await habitService.logHabit(request);
      
      if (response.success) {
        setAlert({ type: 'success', message: response.message || 'Habit logged successfully!' });
        // Reset form
        setSelectedHabit('');
        setSelectedChildHabit('');
        setCount('');
        setComment('');
        // Call success callback
        onSuccess?.();
      } else {
        setAlert({ type: 'error', message: response.error || 'Failed to log habit' });
      }
    } catch (error) {
      setAlert({ type: 'error', message: 'Failed to connect to server' });
    } finally {
      setLoading(false);
    }
  };

  const selectedHabitDef = selectedHabit ? habits[selectedHabit] : null;
  const formatAmount = (num: number) => {
    const formatted = num % 1 === 0 ? num.toFixed(0) : num.toFixed(1);
    return num < 0 ? `(${formatted})` : formatted;
  };
  const childHabitOptions = selectedHabit
    ? Object.keys(habits)
        .filter((habitKey) => habits[habitKey].parentHabitId === selectedHabit)
        .map((habitKey) => ({
          id: habitKey,
          displayName: habits[habitKey].habitDisplayName,
          category: habits[habitKey].category,
        }))
        .sort((a, b) => a.displayName.localeCompare(b.displayName))
    : [];
  const isSelectedHabitParent = selectedHabitDef
    ? ((selectedHabitDef.childHabitIds && selectedHabitDef.childHabitIds.length > 0) || childHabitOptions.length > 0)
    : false;
  const effectiveHabitDef = selectedChildHabit ? habits[selectedChildHabit] : selectedHabitDef;

  if (loadingHabits) {
    return <LoadingSpinner />;
  }

  const habitOptions = Object.keys(habits)
    .filter((habitKey) => habits[habitKey].category?.toLowerCase() === 'manage')
    .filter((habitKey) => !habits[habitKey].parentHabitId)
    .map((habitKey) => ({
      id: habitKey,
      displayName: habits[habitKey].habitDisplayName,
      category: habits[habitKey].category,
    }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Log Habit
        </Typography>
        
        {alert && (
          <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
            {alert.message}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Autocomplete
            fullWidth
            options={habitOptions}
            value={habitOptions.find(opt => opt.id === selectedHabit) || null}
            onChange={(event, newValue) => {
              setSelectedHabit(newValue ? newValue.id : '');
              setSelectedChildHabit('');
            }}
            getOptionLabel={(option) => option.displayName}
            filterOptions={(options, state) => {
              const inputValue = state.inputValue.toLowerCase();
              return options.filter(option => {
                return option.displayName.toLowerCase().includes(inputValue);
              });
            }}
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Typography variant="body2">{option.displayName}</Typography>
                <Chip
                  label={option.category}
                  size="small"
                  color={getCategoryColor(option.category)}
                />
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Habit"
                required
                placeholder="Type to search..."
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />

          {isSelectedHabitParent && (
            <Autocomplete
              fullWidth
              options={childHabitOptions}
              value={childHabitOptions.find(opt => opt.id === selectedChildHabit) || null}
              onChange={(event, newValue) => setSelectedChildHabit(newValue ? newValue.id : '')}
              getOptionLabel={(option) => option.displayName}
              filterOptions={(options, state) => {
                const inputValue = state.inputValue.toLowerCase();
                return options.filter(option => option.displayName.toLowerCase().includes(inputValue));
              }}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                  }}
                >
                  <Typography variant="body2">{option.displayName}</Typography>
                  <Chip
                    label={option.category}
                    size="small"
                    color={getCategoryColor(option.category)}
                  />
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Child Habit"
                  placeholder="Optional"
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          )}

          {effectiveHabitDef && (
            <Box sx={{ p: 2, bgcolor: '#333333', borderRadius: 1 }}>
              {effectiveHabitDef.goal.type === 'THRESHOLD' ? (
                <Typography variant="body2" color="text.secondary">
                  <strong>Goal:</strong> {effectiveHabitDef.goal.value} {effectiveHabitDef.goalUnit} per {effectiveHabitDef.intervalUnit}
                </Typography>
              ) : effectiveHabitDef.goal.type === 'RANGE' ? (
                <Typography variant="body2" color="text.secondary">
                  <strong>Goal Range:</strong> {formatAmount(effectiveHabitDef.goal.min)} – {formatAmount(effectiveHabitDef.goal.max)} {effectiveHabitDef.goalUnit} per {effectiveHabitDef.intervalUnit.toLowerCase()}
                </Typography>
              ) : effectiveHabitDef.goal.type === 'AVERAGE' ? (
                <Typography variant="body2" color="text.secondary">
                  <strong>Average Goal:</strong> {formatAmount(effectiveHabitDef.goal.min)} – {formatAmount(effectiveHabitDef.goal.max)} {effectiveHabitDef.goalUnit} average per {effectiveHabitDef.intervalUnit.toLowerCase()}
                </Typography>
              ) : null}
              <Typography variant="body2" color="text.secondary">
                <strong>Category:</strong> {effectiveHabitDef.category}
              </Typography>
              {effectiveHabitDef.description && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary" component="span" sx={{ fontWeight: 600 }}>
                    Description:
                  </Typography>
                  <Box 
                    sx={{ 
                      mt: 0.5,
                      '& p': { 
                        fontSize: '0.875rem',
                        lineHeight: 1.5,
                        mb: 0.5,
                        color: 'text.secondary',
                      },
                      '& ul, & ol': {
                        pl: 2.5,
                        mb: 0.5,
                        listStyle: 'none',
                      },
                      '& li': {
                        fontSize: '0.875rem',
                        color: 'text.secondary',
                        mb: 0.25,
                      },
                    }}
                  >
                    <MarkdownRenderer>{effectiveHabitDef.description}</MarkdownRenderer>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          <TextField
            fullWidth
            label={`Count/Duration${effectiveHabitDef ? ` (${effectiveHabitDef.goalUnit})` : ''}`}
            type="number"
            value={count}
            onChange={(e) => setCount(e.target.value)}
            inputProps={{
              ...(effectiveHabitDef?.goal.type === 'AVERAGE' ? {} : { min: 0 }),
              step: 0.1,
            }}
            required
          />

          <TextField
            fullWidth
            label="Comment (optional)"
            multiline
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Add any notes about this habit entry..."
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
            sx={{ mt: 2 }}
          >
            {loading ? 'Logging...' : 'Log Habit'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default HabitLogForm;